import AbstractWebRtcCall from "~/ts/library/webRtc/AbstractWebRtcCall";
import {OperatorAccountStore} from "~/operatorApplication/ts/store/OperatorAccountStore";
import OperatorApiRequest from "~/operatorApplication/ts/request/OperatorApiRequest";
import OperatorApplicationBuddyList from "~/operatorApplication/ts/service/OperatorApplicationBuddyList";
import {
    BEFORE_OPERATOR_LOGGED_OUT,
    OperatorApplicationEventManager
} from "~/operatorApplication/ts/OperatorApplicationEventManager";
import {ref, Ref} from "vue";
import {OperatorApplicationConfigInstance} from "~/operatorApplication/ts/service/OperatorApplicationConfig";
import OperatorApplicationPlatformFactory from "~/operatorApplication/ts/platform/OperatorApplicationPlatformFactory";

export default class OperatorApplicationWebRtcCall extends AbstractWebRtcCall {

    private mountedNow: Ref<boolean>;

    getSelfieSegmentationModelRootPath(): string {
        return `assets/operatorApplication/mediapipe/selfieSegmentation/`;
    }

    isSelfieSegmentationAvailable(): boolean {
        return true;
    }

    protected isSelfieSegmentationEnabledByDefault(): boolean {
        return OperatorApplicationConfigInstance.isSelfieSegmentationEnabled;
    }

    toggleSelfieSegmentation() {
        super.toggleSelfieSegmentation();
        OperatorApplicationConfigInstance.isSelfieSegmentationEnabled = this.isUseSelfieSegmentation();
    }

    protected getIceServers(): RTCIceServer[] {
        return OperatorAccountStore.data.value.webRtc.iceServers.map((item) => ({
            urls: item.url,
            username: item.username,
            credential: item.credential
        }));
    }

    showWebRtcNotSupportedError(): void {
    }

    protected getIncomingCallAudioSrc(): string {
        return `${OperatorApplicationConfigInstance.staticServer}/cabinet/assets/operatorApplication/sounds/incomingCall.mp3`;
    }

    public isMobile(): boolean {
        return false;
    }

    private getMountedNowRef(): Ref<boolean> {
        if (!this.mountedNow) {
            this.mountedNow = ref(false);
        }
        return this.mountedNow;
    }

    public isMountedNow(): boolean {
        return this.getMountedNowRef().value;
    }

    public setMountedNow(value: boolean) {
        this.getMountedNowRef().value = value;
    }

    protected async makeHangUp(): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcHangUpOpenApiModel = {
            callId: this.getId()
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/hangUp",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeJoinCall(): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcJoinOpenApiModel = {
            callId: this.getId()
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/join",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    onFinishCall() {
        this.closeNotification();
    }

    protected async makeSendOffer(pairId: number, offer: string): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcSendOfferOpenApiModel = {
            callId: this.getId(),
            pairId,
            offer
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/sendOffer",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeSendLog(logs: string[]): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcSendLogOpenApiModel = {
            callId: this.getId(),
            logs
        };
        (new OperatorApiRequest({
            url: "/webrtc/sendLog",
            p,
            silent: true,
            silentError: true
        })).send();
    }

    protected async makeSendAnswer(pairId: number, answer: string): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcSendAnswerOpenApiModel = {
            callId: this.getId(),
            pairId,
            answer
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/sendAnswer",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeSendIceCandidates(pairId: number, iceCandidates: string[]): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcAddIceCandidatesOpenApiModel = {
            callId: this.getId(),
            pairId,
            iceCandidates
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/addIceCandidates",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeSendDisconnectNotify(pairId: number): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcConnectionBrokenOpenApiModel = {
            callId: this.getId(),
            pairId,
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/connectionBroken",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeSendSuccessConnectNotify(pairId: number): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcConnectionBrokenOpenApiModel = {
            callId: this.getId(),
            pairId,
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/connectionEstablised",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }

    protected async makeChangeUserSettings(isAudioEnabled: boolean, isWebCamEnabled: boolean, isScreenEnabled: boolean): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcChangeUserSettingsOpenApiModel = {
            callId: this.getId(),
            isAudioEnabled,
            isWebCamEnabled,
            isScreenEnabled
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/changeUserSettings",
            p,
            silent: true,
            silentError: true
        })).send();
        if (!result.isSuccess) {
            throw new Error(result.descr);
        }
    }


    public async inviteUser(login: string): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcMakeInviteMethodOpenApiModel = {
            callId: this.getId(),
            login
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/makeInvite",
            p,
            silent: true,
            silentError: true
        })).send();
        result.showMessageOnError();
    }

    protected async makeSendPong(userIdsWithActiveConnections: number[]): Promise<void> {
        let p: OperatorApplicationApi.OperatorWebRtcPongOpenApiModel = {
            callId: this.getId(),
            userIdsWithActiveConnections
        };
        let result = await (new OperatorApiRequest({
            url: "/webrtc/pong",
            p,
            silent: true,
            silentError: true
        })).send();
        result.showMessageOnError();
    }


    protected async onNewCurrentCall() {
        let username = this.getCallData().username;
        await OperatorApplicationBuddyList.openClientByUsername(username);
        OperatorApplicationEventManager.emit(EVENT_OPEN_CALL_TAB, username)
    }

    onNewIncomingCall(): void {
        let text = this.getCallData().users
            .filter(user => !user.isYou)
            .map(user => user.descr)
            .join(', ');
        OperatorApplicationPlatformFactory.notifications.show({
            title: `Входящий звонок от ${text}`,
            text: '',
            click: () => this.onNewCurrentCall(),
            groupId: this.notificationId,
            disallowAutoClose: true,
            actions: [
                {
                    title: 'Принять',
                    click: () => this.joinCall(),
                    color: '#17c671'
                },
                {
                    title: 'Отклонить',
                    click: () => this.hangUp(),
                    color: '#c4183c'
                }
            ]
        });
    }

    private get notificationId() {
        return `call_${this.getId()}`;
    }

    private closeNotification() {
        OperatorApplicationPlatformFactory.notifications.closeByGroupId(this.notificationId);
    }

    removeThisCallFromIncoming() {
        super.removeThisCallFromIncoming();
        this.closeNotification();
    }

    isCallForThisInstance(): boolean {
        let tokenId = this.getYou()?.operatorTokenId;
        return OperatorAccountStore.isAuthorized.value && (!tokenId || OperatorAccountStore.tokenId.value === tokenId);
    }

    getStaticServer(): string {
        return OperatorApplicationConfigInstance.staticServer;
    }
}

OperatorApplicationEventManager.addEventListener(BEFORE_OPERATOR_LOGGED_OUT, () => {
    for (let call of OperatorApplicationWebRtcCall.incomingCalls.value) {
        call.hangUp();
    }
    OperatorApplicationWebRtcCall.setIncomingCalls([]);
    OperatorApplicationWebRtcCall.getCurrentCall()?.hangUp();
});


export const EVENT_OPEN_CALL_TAB = "openWebRtcCallTab";